/**
 * Handing the "Als Mandant anmelden" token (#62) from the IRES Admin Client tab to the new tab that
 * signs in with it, without the token ever entering a URL, storage or the opener relationship.
 *
 * - The admin tab opens the new tab with `noopener`, so it starts with empty session storage. A tab
 *   that keeps its opener gets a copy of the opener's session storage, the Firebase session included,
 *   and would be left signed in as the admin user if the handover failed.
 * - The new tab's URL carries only a random nonce. It announces itself on a `BroadcastChannel` until
 *   the admin tab answers that nonce, once, with the token; both then close the channel.
 * - When IRES Cloud refuses, the admin tab answers with `cancel` and the refusal reason instead. The
 *   refusal often arrives before the new tab has loaded and opened its channel (#2610071: 375 ms), so
 *   the admin tab keeps answering the new tab's announcement until it is heard or the wait ends.
 *
 * The channel only reaches tabs of the same origin, i.e. IRES Office in the same browser profile.
 */

import { CLIENT_LOGIN_REFUSALS, ClientLoginRefusal } from "@/data/models/ClientAdministrationRow";

/** The channel both tabs use. */
export const CLIENT_LOGIN_CHANNEL = "ires-office-client-login";

/** The login route's query key that carries the nonce. */
export const CLIENT_HANDOVER_QUERY_KEY = "clientHandover";

/** How long the admin tab waits for the new tab to announce itself, and the new tab for the token. */
export const CLIENT_HANDOVER_TIMEOUT_MS = 20000;

/** How often the new tab repeats its announcement, in case the admin tab started listening late. */
const ANNOUNCE_INTERVAL_MS = 500;

type HandoverMessage =
  | { type: "ready"; nonce: string }
  | { type: "token"; nonce: string; token: string }
  // IRES Cloud refused: the new tab shows the reason at once instead of failing after the timeout.
  | { type: "cancel"; nonce: string; reason: ClientLoginRefusal | null };

/** The part of `BroadcastChannel` the handover uses; a fake in tests. */
export interface HandoverChannel {
  postMessage(message: HandoverMessage): void;
  close(): void;
  onmessage: ((event: { data: unknown }) => void) | null;
}

export type HandoverChannelFactory = (name: string) => HandoverChannel;

const browserChannel: HandoverChannelFactory = (name) => new BroadcastChannel(name) as unknown as HandoverChannel;

/** A fresh random nonce: 16 random bytes as hex. */
export function newHandoverNonce(): string {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/** Whether a value is a nonce this handover made. */
export function isHandoverNonce(value: unknown): value is string {
  return typeof value === "string" && /^[0-9a-f]{32}$/.test(value);
}

/** The new tab's URL: IRES Office's login route with the nonce (the router uses the hash). */
export function clientHandoverUrl(location: { origin: string; pathname: string }, nonce: string): string {
  return `${location.origin}${location.pathname}#/login?${CLIENT_HANDOVER_QUERY_KEY}=${nonce}`;
}

/** A value as a refusal IRES Office knows, else null. */
function asClientLoginRefusal(value: unknown): ClientLoginRefusal | null {
  return typeof value === "string" && CLIENT_LOGIN_REFUSALS.includes(value as ClientLoginRefusal)
    ? (value as ClientLoginRefusal)
    : null;
}

/** IRES Cloud's refusal reason in a failed call (`details.reason`), else null. */
export function clientLoginRefusalOf(error: unknown): ClientLoginRefusal | null {
  return asClientLoginRefusal((error as { details?: { reason?: unknown } } | null)?.details?.reason);
}

/** What became of the admin tab's offer. */
export type HandoverOutcome = "DELIVERED" | "NO_TAB";

/**
 * The admin tab: waits for the new tab with `nonce` and gives it the token once both are there. Starts
 * listening at once, before the token exists, so an early announcement is not missed.
 *
 * @param nonce - the new tab's nonce.
 * @param token - the token, still being fetched; a rejection ends the offer with that error at once,
 *   while the channel stays open to pass the refusal on to the new tab (see the module comment).
 * @param timeoutMs - how long to wait for the new tab.
 * @param channelFactory - the channel (a fake in tests).
 * @returns DELIVERED, or NO_TAB when the new tab never announced itself (blocked or closed).
 */
export function offerClientLoginToken(
  nonce: string,
  token: Promise<string>,
  timeoutMs = CLIENT_HANDOVER_TIMEOUT_MS,
  channelFactory: HandoverChannelFactory = browserChannel
): Promise<HandoverOutcome> {
  const channel = channelFactory(CLIENT_LOGIN_CHANNEL);

  return new Promise<HandoverOutcome>((resolve, reject) => {
    let tabReady = false;
    let value: string | null = null;
    // Set once IRES Cloud refused: from then on every announcement of the new tab is answered with it.
    let cancel: HandoverMessage | null = null;
    let settled = false;
    let closed = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    // The offer's result and the channel end separately: after a refusal the result is known at once,
    // but the channel stays open for the new tab.
    const settle = (outcome: HandoverOutcome | Error) => {
      if (settled) {
        return;
      }
      settled = true;
      outcome instanceof Error ? reject(outcome) : resolve(outcome);
    };
    const close = () => {
      if (closed) {
        return;
      }
      closed = true;
      if (timer !== null) {
        clearTimeout(timer);
      }
      channel.onmessage = null;
      channel.close();
    };
    const deliverWhenBothThere = () => {
      if (tabReady && value !== null && !closed) {
        channel.postMessage({ type: "token", nonce, token: value });
        close();
        settle("DELIVERED");
      }
    };

    timer = setTimeout(() => {
      close();
      settle("NO_TAB");
    }, timeoutMs);
    channel.onmessage = (event) => {
      const message = event.data as Partial<HandoverMessage> | null;
      if (message?.type !== "ready" || message.nonce !== nonce) {
        return;
      }
      if (cancel !== null) {
        // The new tab started listening after the refusal; this answer is the one it hears.
        channel.postMessage(cancel);
        close();
        return;
      }
      tabReady = true;
      deliverWhenBothThere();
    };
    token.then(
      (received) => {
        value = received;
        deliverWhenBothThere();
      },
      (error: unknown) => {
        if (!closed) {
          cancel = { type: "cancel", nonce, reason: clientLoginRefusalOf(error) };
          // Reaches the new tab if it is already listening; otherwise its next announcement is answered.
          channel.postMessage(cancel);
          if (tabReady) {
            close();
          }
        }
        settle(error instanceof Error ? error : new Error(String(error)));
      }
    );
  });
}

/** What the new tab received: the token, or none with IRES Cloud's refusal when the admin tab sent one. */
export interface HandoverReceipt {
  token: string | null;
  refusal: ClientLoginRefusal | null;
}

/**
 * The new tab: announces `nonce` until the admin tab answers it with the token or a refusal.
 *
 * @param nonce - this tab's nonce, from its URL.
 * @param timeoutMs - how long to wait.
 * @param channelFactory - the channel (a fake in tests).
 * @returns the token; or no token, with the refusal when IRES Cloud refused and null when nothing came.
 */
export function receiveClientLoginToken(
  nonce: string,
  timeoutMs = CLIENT_HANDOVER_TIMEOUT_MS,
  channelFactory: HandoverChannelFactory = browserChannel
): Promise<HandoverReceipt> {
  const channel = channelFactory(CLIENT_LOGIN_CHANNEL);

  return new Promise<HandoverReceipt>((resolve) => {
    let done = false;
    let announcer: ReturnType<typeof setInterval> | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const finish = (receipt: HandoverReceipt) => {
      if (done) {
        return;
      }
      done = true;
      if (announcer !== null) {
        clearInterval(announcer);
      }
      if (timer !== null) {
        clearTimeout(timer);
      }
      channel.onmessage = null;
      channel.close();
      resolve(receipt);
    };

    channel.onmessage = (event) => {
      const message = event.data as Partial<HandoverMessage> | null;
      if (message?.type === "token" && message.nonce === nonce && typeof message.token === "string") {
        finish({ token: message.token, refusal: null });
      } else if (message?.type === "cancel" && message.nonce === nonce) {
        finish({ token: null, refusal: asClientLoginRefusal(message.reason) });
      }
    };
    const announce = () => channel.postMessage({ type: "ready", nonce });
    announcer = setInterval(announce, ANNOUNCE_INTERVAL_MS);
    timer = setTimeout(() => finish({ token: null, refusal: null }), timeoutMs);
    announce();
  });
}

/** The message for a refusal: by its reason, else a general failure. */
export function clientLoginRefusalKey(refusal: ClientLoginRefusal | null): string {
  return refusal ? `admin.clients.clientLogin.refusals.${refusal}` : "admin.clients.clientLogin.failed";
}

/**
 * The message for a failed opening: IRES Cloud's refusal by its reason (the shared IRES admin login, no
 * IRES admin user, the portal client, an IRES Admin app account), else a general failure.
 */
export function clientLoginErrorKey(error: unknown): string {
  return clientLoginRefusalKey(clientLoginRefusalOf(error));
}
